import {keepPreviousData, useInfiniteQuery, useQuery} from '@tanstack/react-query';
import {format} from 'date-fns';
import {
    getCrewDailyJobs,
    getCrews,
    getCustomer,
    getRelatedCustomers,
    getCustomerTermsHistory,
    getCustomerLoginHistory,
    getCustomers,
    getDashboardStats,
    getDailySalesReport,
    getOrganization,
    getOrganizationMembers,
    getRoles,
    getServiceAreas,
    getServiceTemplates,
    getVisits,
    getVisitsInReview,
    getOrganizationByIdentifier,
    getServiceTemplate,
    getNotifications,
    getCustomerSubscriptions,
    getRateRequests,
    getCrewLocations,
    getPicklistItems,
    getNotificationTemplates,
} from '../lib/api';
import {getServiceBundles, calculateBundlePrice} from '../lib/api/routes/service-bundles';
import type {CalculateBundlePriceParams} from '../lib/api/routes/service-bundles';
import {getPaymentPlans} from '../lib/api/routes/payment-plans';
import {crewSuggestionQuery} from './services/useConfigCrewSuggestions';
import {getOrganizationRoles, getPlatformInvoices, getTeamMembers} from '../lib/api/routes/organizations';
import type { Customer, NotificationType, PicklistCategory } from '@ruta/shared';
import { serializeExcludeTypesParam, sortCrewsByNumber } from '@ruta/shared';
import {
    accountFromDetailCache,
    customerDetailQuery,
    isCustomerDetailCache,
    neighborsFromDetailCache,
    pendingAccessRequestsFromDetailCache,
} from '../lib/customerDetailCache';
import {getUserProfile} from '../lib/api/routes/users';
import {
    getCustomerInvoices,
    getWorkQueueSubscriptions,
    listDispatchJobs,
    getVisitsByDispatchJob,
    getAllVisitPages,
} from '../lib/api/routes/visits';
import {getInvoice, getVisitInvoice} from '../lib/api/routes/invoices';
import {getSubcontractorCompanies} from '../lib/api/routes/subcontractor-companies';
import {getUpcomingDeactivations} from '../lib/api/routes/subscriptions';
import {queryKeys} from '../lib/queryKeys';

// Customer Queries - Updated to use new pagination format
export function useCustomers(filters?: any, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.customers.list(filters),
    queryFn: () => getCustomers(
      filters?.status,
      filters?.source,
      filters?.pageSize,
      filters?.page,
      filters?.isActive,
      filters?.query,
      filters?.sortBy,
      filters?.sortOrder,
    ),
    enabled: options?.enabled !== false,
  });
}

/**
 * GET /customers/:id already returns the customer, same-address neighbors, and
 * pending access requests in one payload. The profile used to call that
 * endpoint once per hook (three identical requests on every open). These
 * observers share `customers.detail` and `select` the slice they need.
 *
 * `select` also accepts a raw Customer in cache — a few mutations still write
 * that older shape — so the profile keeps rendering through an optimistic
 * update instead of going blank until refetch.
 */
export function useCustomer(customerId: string) {
  return useQuery({
    ...customerDetailQuery(customerId),
    select: (data): Customer =>
      isCustomerDetailCache(data) ? data.customer : (data as Customer),
    // Keep the previous customer painted while the next id loads (RUT-4534).
    placeholderData: keepPreviousData,
    // Keep the profile's live indicators (portal visit, last activity) fresh:
    // refetch whenever staff return to the tab instead of showing cached times.
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

/**
 * Other customers in the org that share this customer's service address
 * (RUT-2377 AC1). Empty when the customer is the only one at the address.
 */
export function useCustomerAddressNeighbors(customerId: string) {
  return useQuery({
    ...customerDetailQuery(customerId),
    select: neighborsFromDetailCache,
    placeholderData: keepPreviousData,
  });
}

/**
 * Other customers in the org that share a contact phone or email (RUT-4359).
 * Loaded on its own query so the customer page does not wait on it. Empty
 * when this customer is the only one listing those contacts.
 */
export function useRelatedCustomers(customerId: string) {
  return useQuery({
    queryKey: queryKeys.customers.related(customerId),
    queryFn: () => getRelatedCustomers(customerId),
    enabled: !!customerId,
    placeholderData: keepPreviousData,
  });
}

/**
 * Unapproved requests from new people to get service at this customer's address
 * (RUT-2228). Empty when there are none. The owner reviews these and decides
 * whether the requester joins this account or becomes a separate customer.
 */
export function usePendingAccessRequests(customerId: string) {
  return useQuery({
    ...customerDetailQuery(customerId),
    select: pendingAccessRequestsFromDetailCache,
    placeholderData: keepPreviousData,
  });
}

/**
 * The account this customer belongs to, or null. It rides the customer details
 * query, so it costs no request of its own. No placeholder data: another
 * customer's account must never show for this one while it loads.
 */
export function useCustomerAccount(customerId: string) {
  return useQuery({
    ...customerDetailQuery(customerId),
    select: accountFromDetailCache,
  });
}

/** Full history of a customer's terms acceptances (only fetched when opened). */
export function useCustomerTermsHistory(customerId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.customers.termsHistory(customerId),
    queryFn: async () => {
      const response = await getCustomerTermsHistory(customerId);
      return response.acceptances;
    },
    enabled: !!customerId && options?.enabled !== false,
  });
}

/** Full history of a customer's portal logins (only fetched when opened). */
export function useCustomerLoginHistory(customerId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.customers.loginHistory(customerId),
    queryFn: async () => {
      const response = await getCustomerLoginHistory(customerId);
      return response.logins;
    },
    enabled: !!customerId && options?.enabled !== false,
  });
}

export function useCustomerSearch(query: string) {
  return useQuery({
    queryKey: queryKeys.customers.search(query),
    queryFn: () => getCustomers(),
    enabled: !!query && query.length > 2,
  });
}

export function useOrganization(options?: { enabled?: boolean; includeToken?: boolean }) {
  return useQuery({
    queryKey: [...queryKeys.organizations.current(), { includeToken: options?.includeToken }],
    queryFn: () => getOrganization({ includeToken: options?.includeToken }),
    enabled: options?.enabled !== false,
    staleTime: 1000 * 60,
  });
}

export function usePlatformInvoices(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.organizations.platformInvoices(),
    queryFn: getPlatformInvoices,
    enabled: options?.enabled !== false,
    staleTime: 1000 * 60,
  });
}

export function useOrganizationByIdentifier(identifier: string) {
  return useQuery({
    queryKey: queryKeys.organizations.detail(identifier),
    queryFn: () => getOrganizationByIdentifier(identifier),
    enabled: !!identifier,
  });
}

export function useOrganizationMembers(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.organizations.members(),
    queryFn: getOrganizationMembers,
    enabled: options?.enabled !== false,
  });
}

/** The team roster the chat surfaces pick participants from. */
export function useTeamMembers(options?: { enabled?: boolean }) {
    return useQuery({
        queryKey: ['team-members'],
        queryFn: getTeamMembers,
        enabled: options?.enabled !== false,
    });
}

// Roles
export function useRoles(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.roles.list(),
    queryFn: getRoles,
    enabled: options?.enabled !== false,
  });
}

/** Org roles readable by anyone with invite rights (feeds the invite picker, RUT-4122). */
export function useOrganizationRoles(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.roles.orgList(),
    queryFn: getOrganizationRoles,
    enabled: options?.enabled !== false,
  });
}

// Picklist Items
export function usePicklistItems(category: PicklistCategory, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.picklistItems.byCategory(category),
    queryFn: () => getPicklistItems(category),
    enabled: options?.enabled !== false,
    staleTime: 1000 * 60 * 5, // 5 min — these rarely change
  });
}

// Crew Queries
export function useCrews(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.crews.list(),
    // Wrapped, not passed by reference: react-query hands the query function a
    // QueryFunctionContext, which would land in `getCrews`'s options argument.
    queryFn: () => getCrews(),
    enabled: options?.enabled !== false,
    select: (data) => ({
      ...data,
      crews: sortCrewsByNumber(data.crews),
    }),
  });
}

/**
 * Which crews already work this ONE customer's neighbourhood on a service, for
 * the activation dialog (RUT-3316).
 *
 * Goes through `crewSuggestionQuery` — the same request definition the
 * Configuration tab's table uses — so the two surfaces ask the same question and
 * share one cache entry rather than each inventing a shape. Cached separately
 * from `useCrews`: same endpoint, different question, so it must not overwrite
 * the plain crew list every other screen reads.
 */
export function useRecommendedCrews(
  customerId: string | undefined,
  serviceTemplateId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery({
    ...crewSuggestionQuery(serviceTemplateId ?? '', customerId ? [customerId] : []),
    enabled: options?.enabled !== false && !!customerId && !!serviceTemplateId,
  });
}

export function useCrew(crewId: string) {
  return useQuery({
    queryKey: queryKeys.crews.detail(crewId),
    queryFn: async () => {
      const response = await getCrews();
      return response.crews.find(crew => crew.id === crewId);
    },
    enabled: !!crewId,
  });
}

// Service Template Queries
export function useServiceTemplates(options?: { enabled?: boolean; includeInactive?: boolean }) {
  const includeInactive = options?.includeInactive ?? false;
  return useQuery({
    queryKey: queryKeys.serviceTemplates.list({ includeInactive }),
    queryFn: () => getServiceTemplates({ includeInactive }),
    enabled: options?.enabled !== false,
  });
}

export function useServiceTemplate(templateId: string) {
  return useQuery({
    queryKey: queryKeys.serviceTemplates.detail(templateId),
    queryFn: async () => {
      const response = await getServiceTemplate(templateId);
      return response;
    },
    enabled: !!templateId,
  });
}

// Service Bundle Queries
export function useServiceBundles(options?: { enabled?: boolean; includeInactive?: boolean }) {
  const includeInactive = options?.includeInactive ?? false;
  return useQuery({
    queryKey: queryKeys.serviceBundles.list({ includeInactive }),
    queryFn: () => getServiceBundles({ includeInactive }),
    enabled: options?.enabled !== false,
  });
}

/**
 * Live bundle-price preview. Pass the in-flight `pricingPolicy` to preview an
 * unsaved policy. Only runs for a persisted bundle (a bundleId is required).
 */
export function useBundlePrice(
  bundleId: string | undefined,
  params: CalculateBundlePriceParams,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.serviceBundles.price(bundleId ?? '', params),
    queryFn: () => calculateBundlePrice(bundleId as string, params),
    enabled: !!bundleId && options?.enabled !== false,
  });
}

// Payment Plan Queries (RUT-2215)
export function usePaymentPlans(
  customerId: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.paymentPlans.customer(customerId),
    queryFn: () => getPaymentPlans(customerId),
    enabled: !!customerId && options?.enabled !== false,
  });
}

// Visit Queries
export function useVisitsInReview() {
  return useQuery({
    queryKey: ['visits', 'review-queue'],
    queryFn: getVisitsInReview,
  });
}

/** The office review queue: every NEEDS_REVIEW visit, whole set, with row photos. */
export function useVisitsRequiringReview(params?: { enabled?: boolean }) {
  const { enabled = true } = params || {};
  return useQuery({
    queryKey: ['visits', 'requiring-review'],
    queryFn: () => getVisits({ needsReview: true }),
    enabled,
  });
}

/**
 * Every crew's in-progress visit, org-wide (the API returns one per active crew
 * member at most). Powers the Command dashboard's crew-status column — it tells
 * us which crews are working a visit right now. Short staleTime: these change
 * state often.
 */
export function useInProgressVisits(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['visits', 'in-progress', 'all'],
    queryFn: () => getVisits({ status: 'in_progress', fields: 'summary' }),
    staleTime: 15 * 1000,
    enabled: options?.enabled !== false,
  });
}

/**
 * Every crew's paused visits, org-wide (a crew that started a visit then paused
 * it). Small set in practice. Lets the crew-status column flag a crew as paused
 * rather than mislabelling it as idle/next-up.
 */
export function usePausedVisits(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['visits', 'paused', 'all'],
    queryFn: () => getVisits({ status: 'paused', fields: 'summary' }),
    staleTime: 15 * 1000,
    enabled: options?.enabled !== false,
  });
}

/**
 * Next pending visit per crew (not the full org backlog). Powers Live / command
 * next-up without loading thousands of open visits (RUT-3312).
 */
export function usePendingVisits(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['visits', 'pending', 'next-per-crew'],
    queryFn: () => getVisits({ status: 'not_started', fields: 'summary', nextPerCrew: true }),
    staleTime: 30 * 1000,
    enabled: options?.enabled !== false,
  });
}

export function useCrewQueue(crewId?: string) {
  return useQuery({
    queryKey: ['visits', 'crew-queue', crewId],
    queryFn: () => getVisits({ crewId }),
    enabled: !!crewId,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

export function useCrewHistory(crewId: string | undefined, from: string, to: string) {
  return useQuery({
    queryKey: ['visits', 'crew-history', crewId, from, to],
    queryFn: () => getVisits({ crewId, completed: true, from, to }),
    enabled: !!crewId,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

export function useCrewDailyJobs(crewId: string | null, date: string) {
  return useQuery({
    queryKey: ['crews', 'daily-jobs', crewId, date],
    queryFn: () => crewId ? getCrewDailyJobs(crewId, date) : Promise.resolve({ jobs: [], schedule: {} }),
    enabled: !!crewId,
  });
}

// Service Area Queries
export function useServiceAreas(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['service-areas', 'list'],
    queryFn: getServiceAreas,
    enabled: options?.enabled !== false,
  });
}

// User Profile Queries
export function useUserProfile() {
  return useQuery({
    queryKey: queryKeys.users.profile(),
    queryFn: getUserProfile,
  });
}

// Notification Queries
// When WebSocket is available, notifications are pushed in real-time and this query is invalidated
// Polling is only used when WebSocket is not configured
export function useNotifications(
  limit: number = 25,
  showRead: boolean = false,
  isOpen: boolean = false,
  excludeTypes: readonly NotificationType[] = [],
) {
  // Check if WebSocket URL is configured
  // When WebSocket is available, we rely entirely on real-time push + query invalidation
  const hasWebSocketUrl = !!import.meta.env.VITE_WS_URL;
  const excludeKey = serializeExcludeTypesParam(excludeTypes);

  return useInfiniteQuery({
    queryKey: ['notifications', { limit, showRead, excludeTypes: excludeKey }],
    queryFn: ({ pageParam }) => getNotifications(limit, showRead, pageParam as string | undefined, [...excludeTypes]),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextKey,
    // Keep already-loaded pages while a background refetch is in flight so the
    // list height stays stable and the dropdown's scroll position is preserved.
    placeholderData: keepPreviousData,
    // When WebSocket is configured: no polling, rely on WebSocket for real-time updates.
    // While the dropdown is open: pause polling so a background refetch can't rebuild
    // the list and reset scrollTop mid-read; resume once it closes.
    // Without WebSocket and closed: poll every 30 seconds as primary delivery method.
    refetchInterval: (hasWebSocketUrl || isOpen) ? false : 30000,
  });
}

export function useUpcomingDeactivations(withinDays: number = 60) {
  return useQuery({
    queryKey: ['subscriptions', 'upcoming-deactivations', withinDays],
    queryFn: () => getUpcomingDeactivations(withinDays),
    staleTime: 60_000,
    refetchInterval: 5 * 60 * 1000,
  });
}

// Customer Invoices Query
export function useCustomerInvoices(customerId: string, limit?: number) {
  return useQuery({
    queryKey: ['customers', customerId, 'invoices', limit],
    queryFn: () => getCustomerInvoices(customerId, limit),
    enabled: !!customerId,
  });
}

// Admin Visits Query - all pending visits (drains pages; admin tools need the set)
export function useAdminVisits() {
  return useQuery({
    queryKey: ['admin', 'visits', 'pending'],
    queryFn: () => getAllVisitPages({ status: 'not_started', fields: 'summary', limit: 200 }),
  });
}

// Single Invoice Query
export function useInvoice(invoiceId: string) {
  return useQuery({
    queryKey: ['invoices', invoiceId],
    queryFn: () => getInvoice(invoiceId),
    enabled: !!invoiceId,
  });
}

// Visit Invoice Query - fetches invoice data generated from a visit
export function useVisitInvoice(visitId: string) {
  return useQuery({
    queryKey: ['visits', visitId, 'invoice'],
    queryFn: () => getVisitInvoice(visitId),
    enabled: !!visitId,
  });
}

// Customer Subscriptions Query
export function useCustomerSubscriptions(customerId: string) {
  return useQuery({
    queryKey: ['customers', customerId, 'subscriptions'],
    queryFn: () => getCustomerSubscriptions(customerId),
    enabled: !!customerId,
  });
}

// Rate Requests Query
export function useRateRequests(filters?: {
  status?: 'pending' | 'pricing_provided' | 'accepted' | 'declined';
  customerId?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['rate-requests', filters],
    queryFn: () => getRateRequests(filters?.status, filters?.customerId, filters?.limit),
  });
}

// Crew Locations Query
// Initial fetch bootstraps the map; WebSocket handles all real-time updates
// via optimistic cache updates in useWebSocket (no invalidation/refetch)
export function useCrewLocations(crewMemberId?: string) {
  return useQuery({
    queryKey: ['crews', 'locations', crewMemberId],
    queryFn: () => getCrewLocations(crewMemberId),
    refetchInterval: false,
    // Cache the initial fetch; WS updates the cache directly
    staleTime: 1000 * 60 * 5,
  });
}

// Work Queue Queries
export function useWorkQueueSubscriptions(
  date: string,
  serviceTemplateId?: string,
  crewId?: string,
  showUnassigned?: boolean
) {
  return useQuery({
    queryKey: ['work-queue', 'subscriptions', date, serviceTemplateId, crewId, showUnassigned],
    queryFn: () => getWorkQueueSubscriptions(date, serviceTemplateId, crewId, showUnassigned),
    enabled: !!date,
  });
}

// Dashboard Stats Query
// WebSocket invalidates ['dashboard', 'stats'] on visit changes — no polling needed
export function useDashboardStats(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: getDashboardStats,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: options?.enabled !== false,
  });
}

/**
 * Today's Sales drill-down — per-visit breakdown of the day's sales. Enabled
 * lazily (the dialog passes `enabled` so we only fetch when it's open).
 */
export function useDailySalesReport(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['dashboard', 'daily-sales'],
    queryFn: () => getDailySalesReport(),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Fetch recent dispatch jobs for the organization
 */
export function useDispatchJobs(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.dispatchJobs.list(),
    queryFn: () => listDispatchJobs({ limit: 20 }),
    staleTime: 30_000,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Fetch every dispatch job still working visits (newest first, exhaustive).
 * Backed by the sparse active-dispatch index, so it isn't capped to one page
 * like useDispatchJobs — used by the live-ops "Active Dispatches" column.
 */
export function useActiveDispatchJobs(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.dispatchJobs.active(),
    queryFn: () => listDispatchJobs({ status: 'active' }),
    staleTime: 30_000,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Fetch all visits for a specific dispatch job
 */
export function useDispatchJobVisits(dispatchJobId: string | null, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.dispatchJobs.visits(dispatchJobId || ''),
    queryFn: () => getVisitsByDispatchJob(dispatchJobId!),
    staleTime: 30_000,
    enabled: (options?.enabled ?? true) && !!dispatchJobId,
  });
}

/**
 * Fetch all subcontractor companies for the current org
 */
export function useSubcontractorCompanies(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.subcontractorCompanies.list(),
    queryFn: () => getSubcontractorCompanies(),
    staleTime: 60_000,
    enabled: options?.enabled ?? true,
  });
}

// Notification Templates
export function useNotificationTemplates() {
  return useQuery({
    queryKey: queryKeys.notificationTemplates.all(),
    queryFn: getNotificationTemplates,
    staleTime: 1000 * 60 * 5,
  });
}
